// 2026-11-03 - FormuLearning - Step 2 Lab Data 결과 슬롯 사진 (이슈 #112, M1016-04)
// 2026-11-03 수정 - 이슈 #114: viewer 는 사진 추가·삭제 비활성(썸네일·확대 보기는 허용).
// 파일 위치: frontend/src/features/step2/LabImageSlot.tsx
// 회의 261016: 사진 0~n(보통 ≤3), + 로 업로드, 손톱 썸네일, 누르면 확대, 닫기·삭제. PDF 불가(캡처해서 넣는다).
// 이미지는 Bearer 인증 API 라 fetch → Blob → objectURL 로 보인다. 썸네일 objectURL 은 모듈 캐시에 둔다.

import { useEffect, useRef, useState } from 'react'
import { deleteLabImage, fetchLabImageBlob, uploadLabImage } from '../../api/step2'
import type { LabImageMeta } from '../../api/step2'
import { apiErrorMessage } from '../../lib/apiError'
import { toast } from '../../stores/toastStore'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

const ACCEPT = ['image/jpeg', 'image/png', 'image/webp']
const MAX_BYTES = 5 * 1024 * 1024 // 백엔드 `lab_image_service.MAX_BYTES` 와 같다
export const MAX_PER_SLOT = 10    // 백엔드 `MAX_PER_SLOT` 와 같다

const thumbCache = new Map<string, string>()

export function forgetLabThumb(imageId: string) {
  const url = thumbCache.get(imageId)
  if (url) URL.revokeObjectURL(url)
  thumbCache.delete(imageId)
}

/** 업로드 전 거부 사유(없으면 null). 최종 판정은 서버가 내용으로 한다. */
function rejectReason(file: File): string | null {
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) return 'PDF 는 올릴 수 없습니다. 화면을 캡처해서 넣어 주세요.'
  if (!ACCEPT.includes(file.type)) return `${file.name}: JPG·PNG·WEBP 만 올릴 수 있습니다.`
  if (file.size > MAX_BYTES) return `${file.name}: 1장 최대 5 MB 입니다.`
  return null
}

function Thumb({ projectId, image, onOpen }: { projectId: string; image: LabImageMeta; onOpen: () => void }) {
  const [url, setUrl] = useState<string | null>(thumbCache.get(image.id) ?? null)
  useEffect(() => {
    if (url) return
    let alive = true
    fetchLabImageBlob(projectId, image.id, 'thumbnail')
      .then((blob) => {
        const u = URL.createObjectURL(blob)
        thumbCache.set(image.id, u)
        if (alive) setUrl(u)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [projectId, image.id, url])
  return (
    <button
      onClick={onOpen}
      title={image.filename}
      className="h-9 w-9 shrink-0 overflow-hidden rounded bg-bg-input"
      style={{ border: '1px solid var(--border-soft)' }}
    >
      {url && <img src={url} alt={image.filename} className="h-full w-full object-cover" />}
    </button>
  )
}

function Lightbox({ projectId, image, onClose, onDelete, canDelete }: {
  projectId: string; image: LabImageMeta; onClose: () => void; onDelete: () => void; canDelete: boolean
}) {
  const [url, setUrl] = useState<string | null>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    let u: string | null = null
    let alive = true
    fetchLabImageBlob(projectId, image.id, 'original')
      .then((blob) => { u = URL.createObjectURL(blob); if (alive) setUrl(u) })
      .catch((e) => toast(apiErrorMessage(e, '사진을 불러오지 못했습니다.'), 'warn'))
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current() }
    window.addEventListener('keydown', onKey)
    return () => { alive = false; if (u) URL.revokeObjectURL(u); window.removeEventListener('keydown', onKey) }
  }, [projectId, image.id])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        className="flex max-h-[90vh] max-w-[90vw] flex-col rounded p-3"
        style={{ backgroundColor: 'var(--color-bg-panel)', border: '1px solid var(--border-soft)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center gap-3 text-[11px] text-text-secondary">
          <span className="truncate">{image.filename} · {image.width}×{image.height}</span>
          <span className="flex-1" />
          <button onClick={onDelete} disabled={!canDelete} title={canDelete ? undefined : READ_ONLY_TITLE} className="rounded px-2 py-1 text-[11px] text-red-400 hover:bg-bg-input disabled:opacity-40">삭제</button>
          <button onClick={onClose} className="rounded px-2 py-1 text-[11px] hover:bg-bg-input">닫기</button>
        </div>
        <div className="flex min-h-[200px] min-w-[200px] items-center justify-center overflow-auto">
          {url
            ? <img src={url} alt={image.filename} className="max-h-[80vh] max-w-[85vw] object-contain" />
            : <span className="text-[11px] text-text-muted">불러오는 중…</span>}
        </div>
      </div>
    </div>
  )
}

interface Props {
  projectId: string | undefined
  slot: string
  images: LabImageMeta[]
  onChanged: () => void
  /** 업로드 직전 — 슬롯 키(행 uid)가 서버 lab_data 에 저장돼 있도록 보장한다. */
  beforeUpload?: () => Promise<void>
}

export default function LabImageSlot({ projectId, slot, images, onChanged, beforeUpload }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState<LabImageMeta | null>(null)
  const canWrite = useCanWrite() // viewer(#114) — 썸네일·확대 보기는 허용, 추가·삭제는 비활성

  const pick = async (files: FileList | null) => {
    if (!projectId || !files?.length) return
    const list = Array.from(files)
    const room = MAX_PER_SLOT - images.length
    if (list.length > room) toast(`한 칸에 최대 ${MAX_PER_SLOT}장까지 올릴 수 있습니다.`, 'warn')
    setBusy(true)
    try {
      if (beforeUpload) await beforeUpload()
      for (const f of list.slice(0, Math.max(room, 0))) {
        const reason = rejectReason(f)
        if (reason) { toast(reason, 'warn'); continue }
        try {
          await uploadLabImage(projectId, slot, f)
        } catch (e) {
          toast(apiErrorMessage(e, `${f.name}: 업로드하지 못했습니다.`), 'warn')
        }
      }
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
      onChanged()
    }
  }

  const remove = async (image: LabImageMeta) => {
    if (!projectId || !window.confirm('이 사진을 삭제할까요?')) return
    try {
      await deleteLabImage(projectId, image.id)
      forgetLabThumb(image.id)
      setOpen(null)
      onChanged()
    } catch (e) {
      toast(apiErrorMessage(e, '삭제하지 못했습니다.'), 'warn')
    }
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {projectId && images.map((im) => <Thumb key={im.id} projectId={projectId} image={im} onOpen={() => setOpen(im)} />)}
      {images.length < MAX_PER_SLOT && (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={!projectId || busy || !canWrite}
          title={canWrite ? '사진 추가 (JPG·PNG·WEBP, 1장 5 MB 이하 — PDF 는 캡처해서 넣어 주세요)' : READ_ONLY_TITLE}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded text-[14px] text-text-muted hover:bg-bg-input hover:text-text-body disabled:opacity-40"
          style={{ border: '1px dashed var(--border-soft)' }}
        >
          {busy ? '…' : '+'}
        </button>
      )}
      <input ref={inputRef} type="file" accept={ACCEPT.join(',')} multiple hidden onChange={(e) => pick(e.target.files)} />
      {open && projectId && (
        <Lightbox projectId={projectId} image={open} onClose={() => setOpen(null)} onDelete={() => remove(open)} canDelete={canWrite} />
      )}
    </div>
  )
}
